import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Box as MuiBox,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  Popover,
} from '@mui/material';
import { formatAppDateTime, formatAppDateTimeApi } from '../../utils/dateFormats';
import 'jodit/es5/jodit.min.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { exportNote, getNote, sendNoteByEmail } from '../../api/notes';
import { useSnackbar } from 'notistack';
import FindInHtml from '../../components/FindInHtml';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { LinkWithCopy } from '../../components/LinkWithCopy';
import { NoteSelectionActionPopup } from '../../components/NoteSelectionActionPopup';
import { RelatedNotes } from "../../components/RelatedNotes.tsx";
import { Tasks } from '../../components/Tasks';
import { Note } from '../../types/note';
import {
  collectYoutubePreviewCandidates,
  postYoutubeIframeCommand,
  withYoutubeEmbedJsApi,
} from '../../utils/noteMediaHtml';
import {
  isLargePreviewGalleryMode,
  largePreviewButtonFocusSx,
  largePreviewGridRootSx,
  largePreviewGridTileSx,
  largePreviewDialogActionsBarSx,
  largePreviewDialogContentSx,
  largePreviewSideGutterLeftSx,
  largePreviewSideGutterRightSx,
  largePreviewMediaPlayerShellSx,
  largePreviewSideNavBtnSx,
  largePreviewDialogTitleSx,
  largePreviewModalPaperSx,
} from '../../utils/notePreviewUi';
import { LargePreviewDialogTitle, LargePreviewModeToolbar } from '../../utils/LargePreviewKeyboardClue';
import { LargePreviewMediaActions } from '../../utils/LargePreviewMediaActions';
import {
  LARGE_PREVIEW_DIALOG_ATTR,
  useLargePreviewMediaKeyboard,
} from '../../utils/useLargePreviewMediaKeyboard';
import { TagChips } from '../../components/workflow/TagsInput';

type TxtLargePreviewMode = 'rendered' | 'raw' | 'media' | 'grid';

function normalizeJoditTempHtml(input: string): string {
  if (!input) return input;
  // Jodit sometimes wraps iframes with a custom <jodit> element (data-jodit_iframe_wrapper)
  // which can leak into saved HTML and later interfere with playback/interaction.
  return input
    .replace(/<jodit\b/gi, '<div')
    .replace(/<\/jodit>/gi, '</div>');
}

export const NoteView: React.FC = () => {
  const [note, setNote] = useState<Note | null>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [notification, setNotification] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [copyNotification, setCopyNotification] = useState<{ open: boolean; text: string }>({ open: false, text: '' });
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [displayedDescription, setDisplayedDescription] = useState<string>('');
  const [originalDescription, setOriginalDescription] = useState<string | null>(null);
  const [findOpen, setFindOpen] = useState(false);
  const [shortcutsAnchorEl, setShortcutsAnchorEl] = useState<HTMLElement | null>(null);
  const [shortcutFilter, setShortcutFilter] = useState('');
  const [isRefreshingFromFocus, setIsRefreshingFromFocus] = useState(false);
  const noteViewMountedRef = React.useRef(false);
  const descriptionRef = React.useRef<HTMLDivElement | null>(null);
  const [descriptionSelection, setDescriptionSelection] = useState<null | { text: string; top: number; left: number }>(null);
  const [txtLargePreviewOpen, setTxtLargePreviewOpen] = useState(false);
  const [txtLargePreviewMode, setTxtLargePreviewMode] = useState<TxtLargePreviewMode>('rendered');
  const [txtLargePreviewIndex, setTxtLargePreviewIndex] = useState(0);

  const hideDescriptionSelection = useCallback(() => setDescriptionSelection(null), []);

  const updateDescriptionSelection = useCallback(() => {
    try {
      const root = descriptionRef.current;
      if (!root) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        setDescriptionSelection(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const text = sel.toString().trim();
      if (!text) {
        setDescriptionSelection(null);
        return;
      }

      const anchorNode = sel.anchorNode;
      const focusNode = sel.focusNode;
      const inRoot = (node: Node | null) => !!node && root.contains(node);
      if (!inRoot(anchorNode) || !inRoot(focusNode)) {
        setDescriptionSelection(null);
        return;
      }

      const rect = range.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      setDescriptionSelection({
        text,
        top: rect.top - rootRect.top + root.scrollTop,
        left: rect.left - rootRect.left + root.scrollLeft,
      });
    } catch {
      setDescriptionSelection(null);
    }
  }, []);

  const txtPreviewCandidates = useMemo(
    () =>
      collectYoutubePreviewCandidates(
        normalizeJoditTempHtml((displayedDescription || note?.description || '') ?? ''),
      ),
    [displayedDescription, note?.description],
  );

  const txtLargePreviewEmbedUrl = useMemo(() => {
    if (!txtPreviewCandidates.length) return '';
    const idx = Math.min(Math.max(0, txtLargePreviewIndex), txtPreviewCandidates.length - 1);
    const raw = txtPreviewCandidates[idx] || '';
    return withYoutubeEmbedJsApi(raw);
  }, [txtLargePreviewIndex, txtPreviewCandidates]);

  const txtLargePreviewGridRootRef = React.useRef<HTMLDivElement | null>(null);
  const largePreviewIframeRef = React.useRef<HTMLIFrameElement | null>(null);
  const largePreviewGridIframeRef = React.useRef<HTMLIFrameElement | null>(null);

  const bindLargePreviewGridIframeRef = useCallback(
    (idx: number) => (el: HTMLIFrameElement | null) => {
      if (idx === txtLargePreviewIndex) {
        largePreviewGridIframeRef.current = el;
      }
    },
    [txtLargePreviewIndex],
  );

  const openLargePreview = useCallback((preferredMode?: TxtLargePreviewMode) => {
    if (txtPreviewCandidates.length > 0) {
      setTxtLargePreviewMode('media');
    } else {
      setTxtLargePreviewMode(preferredMode ?? 'rendered');
    }
    setTxtLargePreviewIndex(0);
    setTxtLargePreviewOpen(true);
  }, [txtPreviewCandidates.length]);

  useEffect(() => {
    if (!txtLargePreviewOpen) return;
    if (txtLargePreviewMode !== 'grid') return;
    const root = txtLargePreviewGridRootRef.current;
    if (!root) return;
    const id = window.setTimeout(() => {
      try {
        const el = root.querySelector(`[data-grid-index="${txtLargePreviewIndex}"]`) as HTMLElement | null;
        el?.scrollIntoView({ block: 'center', inline: 'center' });
      } catch {
        // ignore
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [txtLargePreviewIndex, txtLargePreviewMode, txtLargePreviewOpen]);

  useEffect(() => {
    if (!txtLargePreviewOpen || txtLargePreviewMode !== 'grid') return;
    const root = txtLargePreviewGridRootRef.current;
    root?.querySelectorAll('iframe').forEach((iframe) => {
      postYoutubeIframeCommand(iframe, 'pauseVideo');
    });
  }, [txtLargePreviewIndex, txtLargePreviewMode, txtLargePreviewOpen]);

  const largePreviewThemed = isLargePreviewGalleryMode(txtLargePreviewMode);

  const {
    context: largePreviewKeyboardContext,
    previewIframeFocusHandlers,
    canYoutubeSpace,
    isYoutubePlaying,
    toggleYoutubePlayback,
  } = useLargePreviewMediaKeyboard({
    open: txtLargePreviewOpen,
    mode: txtLargePreviewMode,
    embedUrl: txtLargePreviewEmbedUrl,
    candidateCount: txtPreviewCandidates.length,
    onIndexChange: setTxtLargePreviewIndex,
    iframeRef: largePreviewIframeRef,
    gridIframeRef: largePreviewGridIframeRef,
    blocked: findOpen || emailDialogOpen,
    onOpenMediaPlayer: () => setTxtLargePreviewMode('media'),
    onOpenMediaGrid: () => setTxtLargePreviewMode('grid'),
  });

  const shortcuts = useMemo(() => ([
    { group: 'view', key: 'Alt+E', desc: t('notes.edit') || 'Edit note' },
    { group: 'view', key: 'Alt+F', desc: t('notes.find') || 'Find in note' },
    { group: 'global', key: 'Alt+N', desc: t('shortcuts.newNote') || 'New note' },
    { group: 'global', key: '/', desc: t('tips.focusSearch') || 'Press / to focus global note search' },
  ]), [t]);

  const openShortcuts = (el: HTMLElement) => setShortcutsAnchorEl(el);
  const closeShortcuts = () => {
    setShortcutsAnchorEl(null);
    setShortcutFilter('');
  };

  // Function to render description as React elements with copyable links and text
  const renderDescriptionAsReact = (html: string) => {
    // Parse HTML string into DOM elements
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const processElement = (element: Element, key: string | number): React.ReactNode => {
      if (element.nodeType === Node.TEXT_NODE) {
        const text = element.textContent || '';
        return processTextWithCopyable(text, key);
      }
      
      if (element.tagName === 'A') {
        const href = element.getAttribute('href') || '';
        // Preserve inner markup (e.g., <mark>) by processing children instead of using textContent
        const children = Array.from(element.childNodes).map((child, index) => {
          if (child.nodeType === Node.TEXT_NODE) {
            return processTextWithCopyable(child.textContent || '', `${key}-text-${index}`);
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            return processElement(child as Element, `${key}-${index}`);
          }
          return null;
        }).filter(Boolean);
        return (
          <LinkWithCopy key={key} href={href}>
            {children}
          </LinkWithCopy>
        );
      }
      
      // Handle other HTML elements
      const children = Array.from(element.childNodes).map((child, index) => {
        if (child.nodeType === Node.TEXT_NODE) {
          return processTextWithCopyable(child.textContent || '', `${key}-text-${index}`);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          return processElement(child as Element, `${key}-${index}`);
        }
        return null;
      }).filter(Boolean);
      
      const Tag = element.tagName.toLowerCase() as keyof JSX.IntrinsicElements;
      const props: any = { key };
      
      // Copy attributes
      for (let i = 0; i < element.attributes.length; i++) {
        const attr = element.attributes[i];
        if (attr.name === 'class') {
          props.className = attr.value;
        } else if (attr.name === 'style') {
          // Parse CSS string into style object
          const styleObj: { [key: string]: string } = {};
          attr.value.split(';').forEach(declaration => {
            const [property, value] = declaration.split(':').map(s => s.trim());
            if (property && value) {
              // Convert kebab-case to camelCase for React
              const camelProperty = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
              styleObj[camelProperty] = value;
            }
          });
          props.style = styleObj;
        } else {
          props[attr.name] = attr.value;
        }
      }
      
      return React.createElement(Tag, props, ...children);
    };
    
    const processTextWithCopyable = (text: string, key: string | number): React.ReactNode => {
      const parts = text.split(/(\[\[[^\]]+\]\])/g);
      return parts.map((part, index) => {
        if (part.startsWith('[[') && part.endsWith(']]')) {
          const copyableText = part.slice(2, -2);
          return (
            <MuiBox 
              key={`${key}-copyable-${index}`}
              component="span" 
              sx={{ 
                display: 'inline-flex', 
                alignItems: 'center',
                backgroundColor: 'action.hover',
                borderRadius: '4px',
                padding: '2px 4px',
              }}
            >
              <MuiBox 
                component="span" 
                data-copyable={copyableText} 
                sx={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.preventDefault();
                  navigator.clipboard.writeText(copyableText).then(() => {
                    setCopyNotification({ open: true, text: copyableText });
                    setTimeout(() => setCopyNotification({ open: false, text: '' }), 7000);
                  }).catch(err => 
                    console.error('Failed to copy text: ', err)
                  );
                }}
              >
                {copyableText}
              </MuiBox>
              <Tooltip title="Copy text">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    navigator.clipboard.writeText(copyableText).then(() => {
                      setCopyNotification({ open: true, text: copyableText });
                      setTimeout(() => setCopyNotification({ open: false, text: '' }), 7000);
                    }).catch(err => 
                      console.error('Failed to copy text: ', err)
                    );
                  }}
                  sx={{ 
                    padding: 0.25, 
                    marginLeft: 0.5,
                    minWidth: 'auto',
                    minHeight: 'auto'
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </MuiBox>
          );
        }
        return part || null;
      }).filter(Boolean);
    };
    
    const bodyChildren = Array.from(doc.body.childNodes);
    return bodyChildren.map((child, index) => {
      if (child.nodeType === Node.TEXT_NODE) {
        return processTextWithCopyable(child.textContent || '', `root-text-${index}`);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        return processElement(child as Element, `root-${index}`);
      }
      return null;
    }).filter(Boolean);
  };



  useEffect(() => {
    const fetchNote = async () => {
      try {
        if (id) {
          const data = await getNote(id);
          setNote(data);
          setOriginalDescription(data.description || '');
          setDisplayedDescription(data.description || '');
        }
      } catch (error) {
        console.error('Error fetching note:', error);
      }
    };

    fetchNote();
  }, [id]);

  useEffect(() => {
    if (!id) {
      return;
    }

    noteViewMountedRef.current = true;

    const handleFocusRefresh = async () => {
      if (!noteViewMountedRef.current || document.visibilityState !== 'visible' || isRefreshingFromFocus) {
        return;
      }

      try {
        setIsRefreshingFromFocus(true);
        const data = await getNote(id);
        const hasChanged = !!note && note.updated_at !== data.updated_at;
        setNote(data);
        setOriginalDescription(data.description || '');
        setDisplayedDescription(data.description || '');
        if (hasChanged) {
          enqueueSnackbar('Note synced from another window', { variant: 'info' });
        }
      } catch {
        // Keep existing note visible if refresh fails.
      } finally {
        setIsRefreshingFromFocus(false);
      }
    };

    window.addEventListener('focus', handleFocusRefresh);
    document.addEventListener('visibilitychange', handleFocusRefresh);

    return () => {
      noteViewMountedRef.current = false;
      window.removeEventListener('focus', handleFocusRefresh);
      document.removeEventListener('visibilitychange', handleFocusRefresh);
    };
  }, [enqueueSnackbar, id, isRefreshingFromFocus, note]);

  useEffect(() => {
    if (note?.name) document.title = 'View ' + t('notes.note') + ` ${note.name}`;
  }, [note]);

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExport = async (type: 'txt' | 'html' | 'pdf') => {
    try {
      if (!id) return;
      const url = await exportNote(id, type);
      const link = document.createElement('a');
      link.href = url;
      link.download = `note-${id}.${type}`;
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting note:', error);
      setNotification({
        open: true,
        message: t('notes.exportError'),
        severity: 'error'
      });
    } finally {
      handleExportClose();
    }
  };

  const handleEmailClick = () => {
    setEmailDialogOpen(true);
  };

  const handleEmailClose = () => {
    setEmailDialogOpen(false);
    setEmail('');
    setSubject('');
    setEmailError('');
    setSubjectError('');
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async () => {
    let isValid = true;
    
    if (!email) {
      setEmailError(t('validation.email.required'));
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError(t('validation.email.invalid'));
      isValid = false;
    } else {
      setEmailError('');
    }

    if (!subject) {
      setSubjectError(t('validation.required'));
      isValid = false;
    } else {
      setSubjectError('');
    }

    if (!isValid) return;

    try {
      if (!id) return;
      setIsLoading(true);
      const response = await sendNoteByEmail(id, email, subject);
      setNotification({
        open: true,
        message: response.status,
        severity: 'success'
      });
      handleEmailClose();
    } catch (error) {
      console.error('Error sending email:', error);
      setNotification({
        open: true,
        message: t('notes.sendEmailError'),
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!id) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      try {
        const target = e.target as HTMLElement | null;
        if (target) {
          const tag = target.tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return; // don't intercept while typing
        }
      } catch (err) { /* ignore */ }

      const key = e.key.toLowerCase();

      // Alt+E / Cmd+E -> edit
      if ((e.altKey && key === 'e') || (e.metaKey && key === 'e')) {
        e.preventDefault();
        if (!isLoading && id) {
          navigate(`/notes/${id}/edit`);
        }
      }

      // Alt+F -> toggle find (open/close)
      if (e.altKey && key === 'f') {
        e.preventDefault();
        setFindOpen(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLoading, id]);

  if (!note) {
    return <Typography>{t('common.loading')}</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={(theme) => ({
        display: 'flex',
        justifyContent: 'space-between',
        mb: 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0,
        position: 'sticky',
        top: isMobile ? '56px' : '64px',
        zIndex: theme.zIndex.appBar + 1,
        backgroundColor: theme.palette.background.paper,
        pt: 1,
        pb: 1,
        px: 0.5,
        boxShadow: '0 14px 21px rgba(0,0,0,0.08)',
        borderBottom: `21px solid ${theme.palette.divider}`,
        transition: 'box-shadow .21s, transform .21s, background-color .21s',
        backdropFilter: 'saturate(1.03) blur(4px)'
      })}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 36, bgcolor: 'primary.main', borderRadius: 1 }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>{note.name}</Typography>
          {!!note.status?.trim() && (
            <Chip
              size="small"
              label={note.status.trim()}
              sx={{ fontWeight: 600 }}
            />
          )}
          {(note.tags ?? []).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              <TagChips tags={note.tags} max={6} />
            </Box>
          )}
          <Tooltip title={`Copy note name: "${note.name}"`}>
            <IconButton
              onClick={() => {
                navigator.clipboard.writeText(note.name).then(() => {
                  setCopyNotification({ open: true, text: note.name });
                  setTimeout(() => setCopyNotification({ open: false, text: '' }), 2000);
                }).catch(err =>
                  console.error('Failed to copy note name: ', err)
                );
              }}
              color="accent"
              size="small"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('notes.lastUpdated', { time: formatAppDateTimeApi(new Date(note.updated_at)) ?? '' })}>
            <IconButton
              size="small"
              color="default"
              sx={{ opacity: 0.65, cursor: 'default' }}
            >
              <AccessTimeIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('notes.find') || 'Find in note'}>
            <IconButton
              size="small"
              color="accent"
              onClick={() => setFindOpen(true)}
              sx={{ ml: 1 }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="text"
            color="accent"
            onClick={() => openLargePreview('rendered')}
            sx={{ ml: 1, textTransform: 'none' }}
          >
            Open Large Preview
          </Button>
              <Tooltip title={t('tips.shortcutsTooltip') || 'Keyboard shortcuts'}>
                <IconButton
                  size="small"
                  color="accent"
                  onClick={(e) => openShortcuts(e.currentTarget)}
                  sx={{ ml: 1 }}
                >
                  <KeyboardIcon />
                </IconButton>
              </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
          <Button
            variant="outlined"
            color="accent"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportClick}
            fullWidth={isMobile}
          >
            {t('notes.export')}
          </Button>
          <Button
            variant="contained"
            color="accent"
            startIcon={<EmailIcon />}
            onClick={handleEmailClick}
            fullWidth={isMobile}
          >
            {t('notes.sendEmail')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/notes/${id}/edit`}
            fullWidth={isMobile}
          >
            {t('common.edit')}
          </Button>
          <Button
            variant="outlined"
            color="accent"
            component={Link}
            to="/notes"
            fullWidth={isMobile}
          >
            {t('common.back')}
          </Button>
        </Box>
      </Box>
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={() => handleExport('txt')}>{t('notes.exportTxt')}</MenuItem>
        <MenuItem onClick={() => handleExport('html')}>{t('notes.exportHtml')}</MenuItem>
        <MenuItem onClick={() => handleExport('pdf')}>{t('notes.exportPdf')}</MenuItem>
      </Menu>

      <Dialog open={emailDialogOpen} onClose={handleEmailClose}>
        <DialogTitle>{t('notes.sendEmail')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('notes.email')}
            type="email"
            fullWidth
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError('');
            }}
            error={!!emailError}
            helperText={emailError}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('notes.subject')}
            type="text"
            fullWidth
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setSubjectError('');
            }}
            error={!!subjectError}
            helperText={subjectError}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailClose}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleEmailSubmit} 
            variant="contained"
            disabled={isLoading}
          >
            {t('notes.send')}
          </Button>
        </DialogActions>
      </Dialog>

      <FindInHtml
        open={findOpen}
        onClose={() => setFindOpen(false)}
        sourceHtml={originalDescription ?? note.description ?? ''}
        inline={!isMobile}
        containerSelector={'.jodit-wysiwyg'}
        onApply={(html) => {
          setOriginalDescription(originalDescription ?? note.description ?? '');
          setDisplayedDescription(html);
        }}
        onClear={() => setDisplayedDescription(originalDescription ?? note.description ?? '')}
      />

      <Popover
        open={Boolean(shortcutsAnchorEl)}
        anchorEl={shortcutsAnchorEl}
        onClose={closeShortcuts}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ p: 1 }}
      >
        <Box sx={{ width: 320, p: 2 }}>
          <TextField
            fullWidth
            placeholder={t('tips.filterPlaceholder') || 'Filter shortcuts'}
            value={shortcutFilter}
            onChange={(e) => setShortcutFilter(e.target.value)}
            size="small"
            sx={{ mb: 1 }}
          />
          <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
            {['view', 'global'].map((group) => {
              const items = shortcuts.filter(s => s.group === group && (() => {
                const f = shortcutFilter.trim().toLowerCase();
                if (!f) return true;
                return s.key.toLowerCase().includes(f) || s.desc.toLowerCase().includes(f);
              })());
              if (!items.length) return null;
              return (
                <Box key={group} sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', pl: 0.5, display: 'block', mb: 0.5 }}>
                    { group === 'view' ? t('shortcuts.editor') : t('shortcuts.global') }
                  </Typography>
                  {items.map((s) => (
                    <Box key={s.key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, alignItems: 'center' }}>
                      <Typography variant="body2">{s.desc}</Typography>
                      <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>{s.key}</Typography>
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('notes.description')}
        </Typography>
        <Box
          className="jodit-wysiwyg"
          ref={descriptionRef}
          onMouseUp={updateDescriptionSelection}
          onKeyUp={updateDescriptionSelection}
          onMouseDown={() => {
            hideDescriptionSelection();
          }}
          sx={{
            position: 'relative',
            // style highlighted matches inserted by FindInHtml (improved)
            '& mark[data-find-index]': {
              backgroundColor: '#00796B', // darker teal for good contrast
              color: '#ffffff', // white text for readability
              //fontSize: '1.12em',
              fontSize: '200%',
              padding: '0 14px',
              borderRadius: '4px',
              display: 'inline-block',
              transition: 'background-color .18s, transform .12s, color .18s',
              transformOrigin: 'center',
              //lineHeight: '1',
            },
            '& mark[data-find-index]:hover': {
              transform: 'scale(1.50)',
              backgroundColor: '#009688', // slightly lighter teal on hover
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
            }
          }}
        >
          {descriptionSelection && (
            <NoteSelectionActionPopup
              selection={descriptionSelection}
              onClose={hideDescriptionSelection}
            />
          )}
          {renderDescriptionAsReact(displayedDescription || note.description)}
        </Box>
      </Paper>

      {/* Large preview dialog (same idea as NoteForm) */}
      <Dialog
        open={txtLargePreviewOpen}
        onClose={() => setTxtLargePreviewOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          [LARGE_PREVIEW_DIALOG_ATTR]: '',
          sx: largePreviewModalPaperSx(largePreviewKeyboardContext, largePreviewThemed),
        }}
      >
        <DialogTitle
          sx={largePreviewDialogTitleSx(largePreviewKeyboardContext, largePreviewThemed)}
        >
          <LargePreviewDialogTitle
            previewMode={txtLargePreviewMode}
            keyboardContext={largePreviewKeyboardContext}
            modeToolbar={
              <LargePreviewModeToolbar
                mode={txtLargePreviewMode}
                onModeChange={setTxtLargePreviewMode}
                showMediaGrid={txtPreviewCandidates.length > 0}
              />
            }
          />
        </DialogTitle>
        <DialogContent
          sx={
            txtLargePreviewMode === 'media'
              ? {
                  ...(largePreviewDialogContentSx(largePreviewKeyboardContext, largePreviewThemed) as any),
                  p: 0,
                  overflow: 'hidden',
                }
              : largePreviewDialogContentSx(largePreviewKeyboardContext, largePreviewThemed)
          }
        >
          {txtLargePreviewMode === 'grid' ? (
            txtPreviewCandidates.length ? (
              <Box ref={txtLargePreviewGridRootRef} sx={largePreviewGridRootSx}>
                {txtPreviewCandidates.map((src, idx) => (
                  <Box
                    key={`${src}-${idx}`}
                    data-grid-index={idx}
                    data-grid-selected={idx === txtLargePreviewIndex ? 'true' : 'false'}
                    sx={largePreviewGridTileSx(idx === txtLargePreviewIndex)}
                    onClick={() => setTxtLargePreviewIndex(idx)}
                  >
                    <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
                      <Box
                        onClick={() => {
                          setTxtLargePreviewIndex(idx);
                          setTxtLargePreviewMode('media');
                        }}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 34,
                          zIndex: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 1,
                          color: 'white',
                          cursor: 'pointer',
                          background: 'linear-gradient(to bottom, rgba(0,0,0,0.72), rgba(0,0,0,0))',
                          userSelect: 'none',
                        }}
                        title="Open large player (O)"
                      >
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          {idx + 1}
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{ textTransform: 'none', minHeight: 24, py: 0, px: 1 }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setTxtLargePreviewIndex(idx);
                            setTxtLargePreviewMode('media');
                          }}
                        >
                          Open (O)
                        </Button>
                      </Box>
                      <iframe
                        ref={bindLargePreviewGridIframeRef(idx)}
                        {...previewIframeFocusHandlers}
                        src={withYoutubeEmbedJsApi(src)}
                        title={`Note media grid ${idx + 1}`}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          border: 0,
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                        allowFullScreen
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ p: 2, color: 'text.secondary' }}>No media to preview.</Box>
            )
          ) : txtLargePreviewMode === 'media' ? (
            txtLargePreviewEmbedUrl ? (
              <Box sx={largePreviewMediaPlayerShellSx(largePreviewKeyboardContext, largePreviewThemed)}>
                <Box sx={largePreviewSideGutterLeftSx(largePreviewKeyboardContext, largePreviewThemed)}>
                  <Tooltip title="Previous (←)">
                    <span style={{ width: '100%', flex: 1, display: 'flex', minHeight: 0 }}>
                      <IconButton
                        disabled={txtLargePreviewIndex <= 0}
                        onClick={() => setTxtLargePreviewIndex((i) => Math.max(0, i - 1))}
                        sx={largePreviewSideNavBtnSx(largePreviewKeyboardContext, largePreviewThemed)}
                        aria-label="Previous video"
                      >
                        <ChevronLeftIcon sx={{ fontSize: 32 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                <Box sx={{ bgcolor: 'black' }}>
                  <iframe
                    ref={largePreviewIframeRef}
                    {...previewIframeFocusHandlers}
                    src={txtLargePreviewEmbedUrl}
                    title="Note media preview"
                    style={{ width: '100%', height: '100%', border: 0 }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                    allowFullScreen
                  />
                </Box>

                <Box sx={largePreviewSideGutterRightSx(largePreviewKeyboardContext, largePreviewThemed)}>
                  <Tooltip title="Next (→)">
                    <span style={{ width: '100%', flex: 1, display: 'flex', minHeight: 0 }}>
                      <IconButton
                        disabled={txtLargePreviewIndex >= txtPreviewCandidates.length - 1}
                        onClick={() =>
                          setTxtLargePreviewIndex((i) =>
                            Math.min(txtPreviewCandidates.length - 1, i + 1),
                          )
                        }
                        sx={largePreviewSideNavBtnSx(largePreviewKeyboardContext, largePreviewThemed)}
                        aria-label="Next video"
                      >
                        <ChevronRightIcon sx={{ fontSize: 32 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 2, color: 'text.secondary' }}>No media to preview.</Box>
            )
          ) : txtLargePreviewMode === 'raw' ? (
            <TextField
              value={normalizeJoditTempHtml(displayedDescription || note?.description || '')}
              fullWidth
              multiline
              minRows={24}
              InputProps={{ readOnly: true }}
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                },
              }}
            />
          ) : (
            <Box
              sx={{
                minHeight: '70vh',
                maxHeight: '70vh',
                overflowY: 'auto',
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
                '& a': { color: 'primary.main' },
              }}
            >
              {renderDescriptionAsReact(
                normalizeJoditTempHtml((displayedDescription || note?.description || '').trim()) ||
                  '<span style="color:#888;">No content to preview.</span>',
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={largePreviewDialogActionsBarSx(largePreviewKeyboardContext, largePreviewThemed)}
        >
          {largePreviewThemed && (
            <LargePreviewMediaActions
              previewMode={txtLargePreviewMode === 'grid' ? 'grid' : 'media'}
              candidateCount={txtPreviewCandidates.length}
              currentIndex={txtLargePreviewIndex}
              onPrev={() => setTxtLargePreviewIndex((i) => Math.max(0, i - 1))}
              onNext={() =>
                setTxtLargePreviewIndex((i) =>
                  Math.min(txtPreviewCandidates.length - 1, i + 1),
                )
              }
              canYoutubeSpace={canYoutubeSpace}
              isYoutubePlaying={isYoutubePlaying}
              onPlayClick={toggleYoutubePlayback}
              keyboardContext={largePreviewKeyboardContext}
              themed={largePreviewThemed}
            />
          )}
          <Button sx={largePreviewButtonFocusSx} onClick={() => setTxtLargePreviewOpen(false)}>
            {t('common.close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('notes.additional')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography>
            {(t('notes.notifyAboutChanges')) + ': '}
            <b>{note.notify_about_changes ? (t('common.yes') || 'Yes') : (t('common.no') || 'No')}</b>
          </Typography>
          <Typography>
            {(t('notes.reminderAt')) + ': '}
            <b>{note.reminder_at ? formatAppDateTime(note.reminder_at) : '-'}</b>
          </Typography>
        </Box>
      </Paper>

      {note.related_notes && note.related_notes.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('notes.relatedNotes')}
            </Typography>
            <RelatedNotes
                note={note}
            />
          </Box>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Copy notification */}
      <Snackbar
        open={copyNotification.open}
        autoHideDuration={7000}
        onClose={() => setCopyNotification({ open: false, text: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert 
          onClose={() => setCopyNotification({ open: false, text: '' })} 
          severity="success"
          sx={{ width: '100%' }}
        >
          ✅ Copied: &quot;{copyNotification.text}&quot;
        </Alert>
      </Snackbar>

      <Box sx={{ mt: 4 }}>
        <Tasks key={String(note.id)} noteId={note.id} />
      </Box>
    </Box>
  );
}; 
